import { HK_WIRE_TRANSFER_STORE_KEY } from "@/interfaces/constants";
import { WireTransferPreOrder } from "@/interfaces/rapydTypes";
import { TransferRecord } from "@/interfaces/transferTypes";
import { cancelWireTransfer, deleteWireTransfer, preSubmitWireTransfer } from "@/service/rapyd.service";
import { fetchTransferRecords } from "@/service/transfer.server";
import { saveRedirectUrl } from "@/utils/storeRedirectUrl";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

export default function useUnfinishedWireTransfer({
	initialRedirect = false,
}: {
	initialRedirect?: boolean;
	initialCancel?: boolean;
} = {}) {
	const router = useRouter();
	const [record, setRecord] = useState<TransferRecord>();
	const [hasRecord, setHasRecord] = useState(false);

	useEffect(() => {
		saveRedirectUrl();
		initial();
	}, []);

	const initial = async () => {
		try {
			const _record = await fetchRecords();
			if (initialRedirect) {
				handleRedirectUrl(_record);
			}
		} catch (error) {
			if (initialRedirect) {
				router.replace("/deposit");
			}
		}
	};

	const handleCancel = async () => {
		try {
			let _record = record;
			if (!hasRecord) {
				_record = await fetchRecords();
			}
			if (!_record) return;

			cancelWireTransfer(_record.transferId);
		} catch (error) {}
	};

	const handleDelete = async () => {
		console.log("handleDelete");
		try {
			let _record = record;
			if (!hasRecord) {
				_record = await fetchRecords();
			}
			if (!_record) return true;

			await deleteWireTransfer(_record.transferId);
			return true;
		} catch (error) {
		} finally {
			sessionStorage.removeItem("depositBankPageHasInteraction");
		}
	};

	const fetchRecords = async () => {
		try {
			const res = await fetchTransferRecords();
			if (!res || res.length === 0) throw new Error("No unfinished records");

			const _record = res[0];

			setRecord(_record);
			setHasRecord(true);

			return _record;
		} catch (error) {
			throw error;
		}
	};

	const updatePreOrder = async (params: WireTransferPreOrder) => {
		try {
			let _record = record;
			if (!hasRecord) {
				_record = await fetchRecords();
			}
			if (!_record) return;

			preSubmitWireTransfer({
				depositId: _record?.transferId || "",
				amount: String(_record?.amount || ""),
				currency: _record?.currency,
				...params,
			});
		} catch (error) {}
	};

	const handleRedirectUrl = (_record?: TransferRecord) => {
		const innerRecord = _record || record;

		try {
			if (!innerRecord || !innerRecord.ext) throw new Error("No unfinished record");

			const ext = JSON.parse(innerRecord.ext);
			const page = ext.page;
			// 1. 银行页面
			if (page === "bank") {
				router.replace(
					`/rapyd/bankAccount?currency=${innerRecord.currency}&amount=${innerRecord.amount}&channelName=${ext.channelName}`,
				);
				return;
			}

			// 2. 凭证页面
			if (page === "credential") {
				router.replace(
					`/rapyd/credentials?currency=${innerRecord.currency}&amount=${innerRecord.amount}&masterAccountId=${ext.depositBankAccountId}&channelName=${ext.channelName}	`,
				);
				return;
			}

			// 3. 自动兑换页面
			if (page === "autoExchange") {
				sessionStorage.setItem(
					HK_WIRE_TRANSFER_STORE_KEY,
					JSON.stringify({
						currency: innerRecord.currency,
						amount: innerRecord.amount,
						depositBankAccountId: ext.depositBankAccountId,
						receiptImages: ext.receiptImages || [],
					}),
				);
				router.push(`/rapyd/autoExchange?depositType=${ext.depositType}`);
				return;
			}

			throw new Error("No unfinished record");
		} catch (error) {
			if (initialRedirect) {
				router.replace("/deposit");
			} else {
				setHasRecord(false);
			}
		}
	};

	return {
		hasRecord,
		setHasRecord,
		record,
		handleCancel,
		handleRedirectUrl,
		createPreOrder: preSubmitWireTransfer,
		updatePreOrder,
		handleDelete,
	};
}
